import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Modal from "../eventModal/Modal";
import EventFileInput from "../EventFileInput";
import { adminURL } from "../../../../../utils/urls";
import Loader from "../../../../loader/FetchLoader";
import { IoArrowBack } from "react-icons/io5";

const UpdateEvent = () => {
  const { eventID } = useParams();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [title, setTitle] = useState("");
  const [venue, setVenue] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("refID");
  const queryClient = useQueryClient();

  // Fetch event data using TanStack Query
  const { data: eventData, isLoading } = useQuery({
    queryKey: ["event", eventID],
    queryFn: async () => {
      const response = await adminURL.get(`events/${eventID}`);
      return response.data.data.event;
    },
  });

  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: async (file) => {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("path", file);

      return axios.post(
        `https://platinumfuse.v2napi.com/api/events/${eventID}/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["event", eventID]);
      setUploadingImage(false);
    },
    onError: () => {
      setModalMessage("Error uploading image");
      setIsSuccess(false);
      setModalVisible(true);
      setUploadingImage(false);
    },
  });

  // Update event mutation
  const updateEventMutation = useMutation({
    mutationFn: async (formData) => {
      return axios.post(
        `https://platinumfuse.v2napi.com/api/events/${eventID}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: () => {
      setModalMessage("Event updated successfully");
      setIsSuccess(true);
      setModalVisible(true);
      queryClient.invalidateQueries(["event", eventID]);

      setTimeout(() => {
        navigate(`/dashboard/events`);
      }, 2000);
    },
    onError: () => {
      setModalMessage("Error in updating event");
      setIsSuccess(false);
      setModalVisible(true);
    },
  });

  // Set initial form data when event data is loaded
  useEffect(() => {
    if (eventData) {
      const dateTime = new Date(eventData.starts_at);
      setDate(dateTime.toISOString().split("T")[0]);
      setTime(dateTime.toTimeString().slice(0, 5));
      setTitle(eventData.name);
      setVenue(eventData.venue);
      setDescription(eventData.description);
    }
  }, [eventData]);

  const handleFileChange = (file) => {
    if (file) {
      uploadImageMutation.mutate(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!date) newErrors.date = "Date is required";
    if (!time) newErrors.time = "Time is required";
    if (!title) newErrors.title = "Title is required";
    if (!venue) newErrors.venue = "Venue is required";
    if (!description) newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    const startsAt = `${date} ${time}`;

    formData.append("starts_at", startsAt);
    formData.append("name", title);
    formData.append("venue", venue);
    formData.append("description", description);
    formData.append("_method", "PATCH");

    updateEventMutation.mutate(formData);
  };

  if (isLoading) {
    return <Loader text="Loading event..." />;
  }

  return (
    <section
      style={{ backgroundColor: "rgba(12, 17, 23, 1)" }}
      className="flex flex-col items-center w-full max-w-[1020px] mx-auto px-4 sm:px-8"
    >
      {modalVisible && (
        <Modal
          message={modalMessage}
          success={isSuccess}
          onClose={() => setModalVisible(false)}
        />
      )}

      <div className="w-full flex justify-start pt-4">
        <button
          onClick={() => navigate("/dashboard/events")}
          className="flex items-center space-x-2 text-white bg-gray-700 px-4 py-2 rounded-lg hover:bg-gray-600"
        >
          <IoArrowBack size={20} />
          <span>Back to Events</span>
        </button>
      </div>

      <form className="w-full space-y-4 pt-8" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          <div className="md:col-span-2 space-y-4">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex flex-col space-y-2">
                <label className="text-white">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="text-white bg-gray-500 py-3 px-2 rounded-xl placeholder-white"
                />
                {errors.date && <p className="text-red-500">{errors.date}</p>}
              </div>
              <div className="flex flex-col space-y-2">
                <label className="text-white">Time</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="text-white bg-gray-500 py-3 px-2 rounded-xl placeholder-white"
                />
                {errors.time && <p className="text-red-500">{errors.time}</p>}
              </div>
            </div>

            <div className="flex flex-col space-y-2">
              <label className="text-white">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-white bg-gray-500 py-3 px-2 rounded-xl placeholder-white w-full sm:w-[400px]"
              />
              {errors.title && <p className="text-red-500">{errors.title}</p>}
            </div>

            <div className="flex flex-col space-y-2">
              <label className="text-white">Venue</label>
              <textarea
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                className="text-white bg-gray-500 py-10 px-2 rounded-xl placeholder-white w-full"
              />
              {errors.venue && <p className="text-red-500">{errors.venue}</p>}
            </div>
          </div>

          <div>
            <div className="flex flex-col space-y-2">
              <label className="text-white">Event Images</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {eventData?.images && eventData.images.length > 0 ? (
                  eventData.images.map((image, index) => (
                    <div key={image.id} className="relative">
                      <img
                        src={image.url}
                        alt={`Event  ${index + 1}`}
                        className="rounded-lg w-full h-32 object-cover"
                      />
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg w-full h-32 bg-gray-700 flex items-center justify-center">
                    <p className="text-white">No images available</p>
                  </div>
                )}
              </div>
              <div className="flex flex-col space-y-2">
                <p className="text-white text-sm">
                  Add new image (Max size: 5MB, Formats: JPG, PNG)
                </p>
                {uploadingImage ? (
                  <div className="flex items-center space-x-2 text-white">
                    <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div>
                    <span>Uploading...</span>
                  </div>
                ) : (
                  <EventFileInput onFileChange={handleFileChange} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          <label className="text-white">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="text-white bg-gray-500 py-10 px-2 rounded-xl w-full placeholder-white"
          />
          {errors.description && (
            <p className="text-red-500">{errors.description}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center py-8 space-y-4 sm:space-y-0">
          <button
            onClick={() => navigate("/dashboard/events")}
            type="button"
            className="border text-white py-2 px-10 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-amber-500 text-black py-2 px-10 rounded-md"
            disabled={updateEventMutation.isPending || uploadingImage}
          >
            {updateEventMutation.isPending ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default UpdateEvent;
